import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProgramTypes, toProgramTypes } from '@k12/models';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Approved copy per program type. Household and Provider registration have no approved wording yet,
 * so they deliberately fall through to GENERIC_CLOSED_MESSAGE rather than rendering an empty body.
 *
 * The years are fixed rather than derived from the wall clock: the academic year turns in July, so
 * `new Date().getFullYear()` arithmetic would roll this copy over on 1 January. If these need to
 * move, the source is `academicYear` on the enrollment status response, not the current date.
 */
const CLOSED_MESSAGES: Partial<Record<ProgramTypes, string>> = {
  [ProgramTypes.School]:
    'The 2026-27 registration window for new schools has now closed. We invite you to <a href="https://k12.ncseaa.edu/school-admins/new-school-registration/" target="_blank">register</a> for the 2027-28 school year, which will open in February 2027.',
  [ProgramTypes.EsaPlusApplication]:
    'The 2026-27 ESA+ new student application period has ended. We welcome you to apply in February ' +
    '2027 for the 2027-2028 school year. Students must meet <a href="https://k12.ncseaa.edu/the-education-student-accounts/how-to-apply/" target="_blank">program eligibility requirements</a> to be ' +
    'considered for an award.',
  [ProgramTypes.EsaPlusRenewal]:
    'The ESA+ Scholarship renewal period has ended. We welcome you to re-apply as a <a href="https://k12.ncseaa.edu/the-education-student-accounts/how-to-apply/" target="_blank">new student applicant</a> during the next cycle.',
  [ProgramTypes.OsApplication]:
    'The 2026-27 Opportunity Scholarship new student application period has ended. We welcome you to ' +
    'apply in February 2027 for the 2027-2028 school year. Students must meet <a href="https://k12.ncseaa.edu/opportunity-scholarship/how-to-apply/" target="_blank">program eligibility requirements </a>to be considered for an award.',
  [ProgramTypes.OsRenewal]:
    'The Opportunity Scholarship renewal period has ended. We welcome you to re-apply as a <a href="https://k12.ncseaa.edu/opportunity-scholarship/how-to-apply/" target="_blank">new student applicant</a> during the next cycle.'
};

export const GENERIC_CLOSED_MESSAGE =
  'This registration period has closed. Please check back for the next enrollment window.';

@Component({
  selector: 'app-closed-enrollment',
  imports: [],
  templateUrl: './closed-enrollment.component.html',
  styleUrl: './closed-enrollment.component.scss'
})
export class ClosedEnrollmentComponent {
  public closedEnrollmentTitle = 'Thank you for your interest';
  public closedEnrollmentMessage: SafeHtml = GENERIC_CLOSED_MESSAGE;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

  constructor() {
    // A stream rather than a snapshot read: /closed has no runGuardsAndResolvers, so a guard
    // redirect into an already-mounted /closed with a different programType would otherwise keep
    // showing the previous program's copy.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      // toProgramTypes accepts either the backend key ('School') or the label
      // ('School Application'), so the guard can pass whichever it holds.
      this.closedEnrollmentMessage = this.sanitizer.bypassSecurityTrustHtml(this.resolveClosedMessage(params.get('programType')));
    });
  }

  private resolveClosedMessage(programType: string | null): string {
    const program = programType ? toProgramTypes(programType) : null;
    return (program && CLOSED_MESSAGES[program]) || GENERIC_CLOSED_MESSAGE;
  }
}
